// react
import React, { useEffect, useState } from "react";
import { useNavigate, useParams, useMatch } from "react-router-dom";
import { useSelector } from "react-redux";

//Formik
import { useFormik, FormikProvider } from "formik";

// MUI Components
import {
  Box,
  Grid,
  Typography,
  TextField,
  Button,
  Radio,
  RadioGroup,
  FormControlLabel,
  FormControl,
  Stack,
  Select,
  MenuItem,
  Divider,
  Modal,
} from "@mui/material";
import { LoadingButton } from "@mui/lab";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import TextareaAutosize from "@mui/material/TextareaAutosize";
import { alpha, styled, useTheme } from "@mui/material/styles";

// custom components, constants and services
import { tagFormValidationSchema } from "src/util/validationSchemas";
import { getTagDefaultValues, tagFormChargersColumns } from "./Tags.constants";
import { Loader } from "../Loader";

//custom hooks
import { useAccess } from "src/hooks/useAccess.hook";
import { useUser } from "src/hooks/useUser.hook";
import { useMutate, useGetDetail } from "src/hooks/useFetch.hook";
import { useSelectedTeam, useSelectedTeamState } from "src/hooks/useSelectedTeam";
import {
  getTeamUrl,
  tagIdUrl,
  tagUrl,
  tagTopupHistoryUrl,
} from "src/react-query/endPoints";
import { is } from "src/util/is";
import sanitizeValues from "src/utils/sanitizeValues";
import { FEATURE } from "src/util/enums";
import Token from "src/util/Token";
import { useAuth } from "src/hooks/useAuth.hook";
import { fetchParentTags } from "src/utils/api";
import { BaseTable } from "../BaseTable/BaseTable";
import { RootTabItemStyles, RootTabStyle } from "src/theme/overrides/Tabs";
import { TabContext } from "@mui/lab";

const StyledTextarea = styled(TextareaAutosize)(({ theme }) => ({
  width: "100%",
  borderRadius: 8,
  padding: 12,
  fontFamily: "inherit",
  fontSize: 16,
  border: `1px solid ${theme.palette.divider}`,
  marginTop: 8,
  backgroundColor:
    theme.palette.mode === "dark"
      ? alpha(theme.palette.background.paper, 0.9)
      : theme.palette.background.paper,
  color: theme.palette.text.primary,
  transition: theme.transitions.create(["border-color", "background-color"], {
    duration: theme.transitions.duration.shortest,
  }),
  resize: "vertical",
  "&:focus": {
    outline: "none",
    borderColor: theme.palette.primary.main,
    backgroundColor: alpha(theme.palette.primary.main, 0.08),
  },
}));

function TagForm() {
  const theme = useTheme();
  const primaryMain = theme.palette.primary.main;
  const primaryHover = theme.palette.primary.dark;
  const primaryContrast = theme.palette.primary.contrastText;
  const paperBorder = alpha(
    theme.palette.divider,
    theme.palette.mode === "dark" ? 0.6 : 0.2
  );
  const balanceHighlightBg = alpha(
    theme.palette.mode === "dark" ? theme.palette.primary.light : primaryMain,
    theme.palette.mode === "dark" ? 0.25 : 0.08
  );
  const navigate = useNavigate();
  const { teamId } = useSelectedTeam();
  const { teamCurrencySymbol } = useSelectedTeamState();
  const isCreateTag = useMatch("/tag/new");
  const { data: userData } = useUser();
  const { tagId } = useParams();
  const hasReadOnlyAccess = useAccess(FEATURE.TAG, "R");

  const isReadOnly = hasReadOnlyAccess && !isCreateTag;

  const allowManualBalanceEdit =
    userData?.data?.company?.advance_settings?.allow_manual_balance_edit;

  const minWalletBalance = userData?.data?.company?.minimumWalletBalance;
  const {
    data,
    isFetching,
    refetch: refetchTag,
  } = useGetDetail("tag", { id: tagId });
  const { data: tagUsers } = useGetDetail("tagsUser", { id: tagId });

  const { data: tagsChargerList, isLoading: isTagsChargerFetching } =
    useGetDetail("tagsCharger", { id: tagId });

  const { tabOpened, tabOpenedFor, detailsId } = useSelector(
    ({ tabsCurrentlyOpenedFor }) => tabsCurrentlyOpenedFor
  );
  const { formData, formDataOf } = useSelector(({ formData }) => formData);
  const { mutate } = useMutate("tags");

  const onSubmit = async (tagData, { setSubmitting }) => {
    const sanitizeData = sanitizeValues(tagData);
    sanitizeData["expirydate"] = tagData.expirydate;
    sanitizeData.tag_type = "RFID";
    sanitizeData.is_parent_tag = sanitizeData.tagType === "parent" ? 1 : 0;
    delete sanitizeData.tagType;
    const mutateObject = {
      url: getTeamUrl(teamId, tagUrl),
      method: "POST",
    };
    if (!is.nullOrUndefined(tagId) && !isCreateTag) {
      mutateObject.method = "PUT";
      mutateObject.url = getTeamUrl(teamId, tagIdUrl(tagId));
    }
    sanitizeData.companyId = teamId;
    if (sanitizeData.expirydate) {
      const expiryDate = new Date(sanitizeData.expirydate);
      expiryDate.setDate(expiryDate.getDate() + 1);
      sanitizeData.expirydate = expiryDate.toISOString().split("T")[0];
    }

    if (values.tagType === "parent") {
      sanitizeData.reserve_amount = reserveAmount;
    } else {
      sanitizeData.reserve_amount = null;
    }

    mutate(
      {
        url: mutateObject.url,
        method: mutateObject.method,
        body: sanitizeData,
      },
      { onSuccess }
    );
    setSubmitting(false);
  };

  const onSuccess = () => {
    if (
      formDataOf === "customer" &&
      !is.empty(formData) &&
      tabOpenedFor === "customer" &&
      tabOpened === "tags"
    ) {
      !is.empty(detailsId)
        ? navigate(`/customer/${detailsId}`)
        : navigate(`/customer/new`);
      return;
    }
    navigate("/tag");
  };

  const formik = useFormik({
    initialValues: getTagDefaultValues(teamId, userData?.data?.id),
    validationSchema: tagFormValidationSchema,
    onSubmit,
  });

  const {
    touched,
    errors: formikErrors,
    values,
    handleSubmit,
    isSubmitting,
    getFieldProps,
    setFieldValue,
    resetForm,
  } = formik;
  const user = useAuth();
  const [maxActiveType, setMaxActiveType] = useState("block");
  const [customLimit, setCustomLimit] = useState("");
  const [form, setForm] = useState("");
  const [errors, setErrors] = useState({});
  const [loadingTopup, setLoadingTopup] = useState(false);
  const [parentTags, setParentTags] = useState([]);
  const [loadingParentTags, setLoadingParentTags] = useState(false);
  const [selectedParentTag, setSelectedParentTag] = useState("");
  const [topupModalOpen, setTopupModalOpen] = useState(false);
  const [topupAmount, setTopupAmount] = useState("");
  const [topupInProgress, setTopupInProgress] = useState(false);
  const [tabIndex, setTabIndex] = useState(0);
  const [childTags, setChildTags] = useState([]);
  const [reserveAmount, setReserveAmount] = useState("");
  const [topupHistory, setTopupHistory] = useState([]);
  const [loadingTopupHistory, setLoadingTopupHistory] = useState(false);

  useEffect(() => {
    if (data) {
      const tag = data[0];

      if (
        tag.expirydate &&
        typeof tag.expirydate === "string" &&
        tag.expirydate.trim() !== ""
      ) {
        tag.expirydate = new Date(tag.expirydate);
        if (isNaN(tag.expirydate.getTime())) tag.expirydate = null;
      } else {
        tag.expirydate = null;
      }

      if (is.null(tag.emailId)) tag.emailId = "";

      let uiTagType;
      if (tag.tag_type === "RFID" && tag.is_parent_tag === 1) {
        uiTagType = "parent";
      } else if (tag.tag_type === "RFID" && tag.parenttag) {
        uiTagType = "child";
      } else {
        uiTagType = "standalone";
      }

      resetForm({ values: { ...tag, tagType: uiTagType } });
      setSelectedParentTag(tag.parenttag || "");
      setFieldValue("parenttag", tag.parenttag || "");

      const maxCount =
        tag.max_active_trans_count !== undefined
          ? tag.max_active_trans_count
          : tag.max_active_count;

      if (maxCount === 0) {
        setMaxActiveType("block");
        setCustomLimit("");
      } else if (maxCount < 0) {
        setMaxActiveType("unlimited");
        setCustomLimit("");
      } else if (typeof maxCount === "number" && maxCount > 0) {
        setMaxActiveType("custom");
        setCustomLimit(maxCount.toString());
      } else {
        setMaxActiveType("block");
        setCustomLimit("");
      }
      setFieldValue("max_active_trans_count", maxCount);
      const childTagsWithId = (tag.childTags ?? []).map((child, idx) => ({
        ...child,
        tag: child.id_tag,
        id: child.id_tag || idx,
      }));
      setChildTags(childTagsWithId);
      if (tag.reserve_amount !== undefined) {
        setReserveAmount(tag.reserve_amount);
      }
    }
  }, [data, resetForm, setFieldValue]);

  useEffect(() => {
    setLoadingParentTags(true);
    fetchParentTags(teamId)
      .then(setParentTags)
      .finally(() => setLoadingParentTags(false));
  }, [teamId]);

  useEffect(() => {
    if (data) {
      const [tag] = data;
      if (tag.expirydate) tag.expirydate = new Date(tag.expirydate);
      if (is.null(tag.expirydate)) tag.expirydate = null;
      if (is.null(tag.emailId)) tag.emailId = "";
    }
  }, [data, resetForm, setFieldValue]);

  useEffect(() => {
    if (values.tagType !== "parent" && tabIndex === 3) {
      setTabIndex(0);
    }
  }, [values.tagType, tabIndex]);

  useEffect(() => {
    if (isReadOnly && topupModalOpen) {
      setTopupModalOpen(false);
    }
  }, [isReadOnly, topupModalOpen]);

  useEffect(() => {
    const fetchTopupHistory = async () => {
      setLoadingTopupHistory(true);
      try {
        const token = Token.get();
        const res = await fetch(tagTopupHistoryUrl(teamId, tagId), {
          headers: {
            ...(token && { Authorization: `Bearer ${token}` }),
          },
        });
        const history = await res.json();
        setTopupHistory(history);
      } catch (e) {
        setTopupHistory([]);
      }
      setLoadingTopupHistory(false);
    };
    if (tabIndex === 4 && tagId) fetchTopupHistory();
  }, [tabIndex, tagId, teamId]);

  const getMaxActiveHelperText = () => {
    switch (maxActiveType) {
      case "block":
        return "This tag will be blocked and cannot start any transactions.";
      case "unlimited":
        return "This tag can start unlimited concurrent transactions.";
      case "custom":
        return "Set the maximum number of concurrent transactions allowed for this tag.";
      default:
        return "";
    }
  };

  const handleTagTypeChange = (e) => {
    setFieldValue("tagType", e.target.value);
    if (e.target.value !== "child") setSelectedParentTag("");
  };

  const handleMaxActiveTypeChange = (e) => {
    setMaxActiveType(e.target.value);
    if (e.target.value !== "custom") setCustomLimit("");
    if (e.target.value === "block") setFieldValue("max_active_trans_count", 0);
    else if (e.target.value === "unlimited")
      setFieldValue("max_active_trans_count", -1);
    else setFieldValue("max_active_trans_count", customLimit || "");
  };

  const handleCustomLimitChange = (e) => {
    setCustomLimit(e.target.value);
    setFieldValue("max_active_trans_count", e.target.value);
  };

  const handleParentTagChange = (e) => {
    setSelectedParentTag(e.target.value);
    setFieldValue("parenttag", e.target.value);
  };

  const handleDateChange = (date) => {
    setFieldValue("expirydate", date);
  };

  const handleNotesChange = (e) => {
    setFieldValue("note", e.target.value);
  };

  if (isFetching) return <Loader />;

  const handleTopup = async (amount) => {
    if (isReadOnly) return;
    if (!amount) {
      setErrors({ balance: "Enter top-up amount" });
      return;
    }
    
    // Validate minimum wallet balance requirement
    if (minWalletBalance) {
      const currentBalance = parseFloat(values.balance_amount || 0);
      const topupValue = parseFloat(amount || 0);
      const minRequired = parseFloat(minWalletBalance || 0);
      const newBalance = currentBalance + topupValue;
      
      if (newBalance < minRequired) {
        const shortfall = minRequired - currentBalance;
        setErrors({ balance: `You cannot add less than ₹${shortfall} because your balance after topup (₹${newBalance.toFixed(2)}) must meet the minimum wallet balance requirement of ₹${minRequired}` });
        return;
      }
    }
    
    try {
      setErrors({});
      setLoadingTopup(true);
      const token = Token.get();
      const res = await fetch(
        getTeamUrl(teamId, `${tagUrl}/${values.id}/topup`),
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            ...(token && { Authorization: `Bearer ${token}` }),
          },
          body: JSON.stringify({
            amount: Number(amount) * 100,
            currency: "INR",
          }),
        }
      );
      const data = await res.json();
      setLoadingTopup(false);
      if (data.success && data.order) {
        openRazorpayCheckout(data.order);
      } else {
        setErrors({ api: data.message || "Failed to create order" });
      }
    } catch (err) {
      setLoadingTopup(false);
      setErrors({ api: "Top-up failed." });
    }
  };

  function openRazorpayCheckout(order) {
    const options = {
      key: process.env.REACT_APP_ZYNKA_RAZORPAY_KEY,
      amount: order.amount,
      currency: order.currency,
      order_id: order.id,
      name: "RFID Tag Topup",
      description: `Topup for tag ${form.tag}`,
      handler: function (response) {
        confirmTopup(response.razorpay_payment_id, order.amount / 100);
      },
      prefill: {
        email: user?.user?.email,
      },
      theme: { color: primaryMain },
    };
    const rzp = new window.Razorpay(options);
    rzp.open();
  }

  async function confirmTopup(paymentId, amount) {
    try {
      setTopupInProgress(true);
      const token = Token.get();
      const res = await fetch(
        getTeamUrl(teamId, `/tag/${values.id}/topup/confirm`),
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            ...(token && { Authorization: `Bearer ${token}` }),
          },
          body: JSON.stringify({ paymentId, amount }),
        }
      );

      const result = await res.json();

      if (result.success) {
        const updated = await refetchTag();
        const backendBalance =
          Array.isArray(updated?.data) && updated.data.length > 0
            ? updated.data[0].balance_amount
            : updated?.data?.balance_amount ?? undefined;

        if (typeof backendBalance === "number") {
          setFieldValue("balance_amount", backendBalance);
        } else {
          console.warn("Could not find balance in backend response");
        }

        setTopupModalOpen(false);
        setTopupAmount("");
        setTopupInProgress(false);
      } else {
        console.error("Topup error:", result.message);
        setErrors({ api: result.message || "Failed to confirm top-up" });
        setTopupInProgress(false);
      }
    } catch (err) {
      console.error("Topup exception:", err);
      setErrors({ api: "Top-up confirmation failed." });
      setTopupInProgress(false);
    }
  }

  const calculateEffectiveTopupValue = (topupAmount) => {
    return topupAmount - (topupAmount * 2.36 / 100);
  }

  const canInitiateTopup = !isReadOnly && values.tagType !== "child";

  console.log("allowManualBalanceEdit", allowManualBalanceEdit);
console.log("tagType", values.tagType);

  return (
    <Box
      sx={{
        minHeight: "100vh",
        backgroundColor: theme.palette.background.default,
        py: { xs: 2, md: 4 },
        px: { xs: 1, md: 4 },
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "flex-start",
        transition: theme.transitions.create("background-color", {
          duration: theme.transitions.duration.shorter,
        }),
      }}
    >
      <Box sx={{ width: "100%", maxWidth: 1400, mb: 2 }}>
        <TabContext value={tabIndex.toString()}>
          <Stack
            direction="row"
            justifyContent="center"
            alignItems="center"
            sx={{ width: "100%" }}
          >
            <RootTabStyle
              indicatorColor="transparent"
              centered
              length={5}
              value={tabIndex.toString()}
              onChange={(_, newValue) => setTabIndex(Number(newValue))}
              sx={{
                "& .MuiTabs-indicator": {
                  backgroundColor: primaryMain,
                },
              }}
            >
              <RootTabItemStyles value="0" label="RFID" />
              <RootTabItemStyles value="1" label="EV Driver" />
              <RootTabItemStyles value="2" label="Attached Chargers" />
              {values.tagType === "parent" && (
                <RootTabItemStyles value="3" label="Child Tags" />
              )}
              <RootTabItemStyles value="4" label="Topup History" />
            </RootTabStyle>
          </Stack>
        </TabContext>
      </Box>

      <Box
        component="form"
        onSubmit={handleSubmit}
        autoComplete="off"
        sx={{
          width: "100%",
          maxWidth: 1400,
          backgroundColor: theme.palette.background.paper,
          borderRadius: 2.5,
          boxShadow: theme.customShadows?.z8 ?? 2,
          border: `1px solid ${paperBorder}`,
          p: { xs: 2, md: 4 },
          transition: theme.transitions.create(
            ["background-color", "box-shadow"],
            { duration: theme.transitions.duration.shorter }
          ),
        }}
      >
        <FormikProvider value={formik}>
          {tabIndex === 0 && (
            <Grid container spacing={2}>
              {/* ID Tag */}
              <Grid item xs={12} md={6}>
                <Typography sx={{ fontWeight: 600, mb: 1 }}>
                  ID Tag <span style={{ color: "red" }}>*</span>
                </Typography>
                <TextField
                  required
                  fullWidth
                  placeholder="Enter ID Tag"
                  {...getFieldProps("tag")}
                  error={Boolean(touched.tag && errors.tag)}
                  helperText={touched.tag && errors.tag}
                  sx={{ mb: { xs: 2, md: 0 } }}
                  disabled={!isCreateTag}
                />
              </Grid>
              {/* Balance Amount */}
              <Grid item xs={12} md={6}>
                <Typography sx={{ fontWeight: 600, mb: 1 }}>
                  Balance Amount
                </Typography>
                <Stack direction="row" spacing={2} alignItems="center">
                  <TextField
                    fullWidth
                    placeholder="Enter Balance Amount"
                    {...getFieldProps("balance_amount")}
                    sx={{
                      mb: 0,
                      "& .MuiFormHelperText-root": {
                        marginLeft: 0,
                        textAlign: "left",
                      },
                    }}
                    disabled={
                      !(allowManualBalanceEdit && values.tagType === "parent")
                    }
                    error={Boolean(
                      touched.balance_amount && formikErrors.balance_amount
                    )}
                    helperText={
                      touched.balance_amount && formikErrors.balance_amount
                    }
                  />
                  {!isReadOnly && !allowManualBalanceEdit && (
                    <Button
                      variant="contained"
                      sx={{
                        bgcolor: primaryMain,
                        color: primaryContrast,
                        borderRadius: 2,
                        fontWeight: 600,
                        px: 3,
                        minWidth: 100,
                        height: 40,
                        "&:hover": { bgcolor: primaryHover },
                      }}
                      onClick={() => {
                        if (!canInitiateTopup) return;
                        setTopupModalOpen(true);
                      }}
                      disabled={!canInitiateTopup}
                    >
                      Topup
                    </Button>
                  )}
                </Stack>

                {/* Show warning when current balance is below minimum wallet balance */}
                {minWalletBalance && parseFloat(values.balance_amount || 0) < parseFloat(minWalletBalance) && (
                  <Typography
                    variant="caption"
                    sx={{
                      mt: 1,
                      color: theme.palette.error.main,
                      display: "block",
                      fontWeight: 500,
                      fontSize: 12
                    }}
                  >
                    Current balance ({teamCurrencySymbol}{parseFloat(values.balance_amount || 0).toFixed(2)}) is below minimum wallet balance requirement ({teamCurrencySymbol}{minWalletBalance}). Please add ({teamCurrencySymbol}{(parseFloat(minWalletBalance) - parseFloat(values.balance_amount || 0)).toFixed(2)}) or more.
                  </Typography>
                )}

                {/* Optional helper messages for tag types */}
                {values.tagType === "parent" && (
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ mt: 1 }}
                  >
                    Parent tag balance can only be updated via TOPUP
                    functionality. This can be changed in Company Settings.
                  </Typography>
                )}

                {values.tagType === "child" && (
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ mt: 1 }}
                  >
                    Child tags inherit balance from their parent tag.
                  </Typography>
                )}
              </Grid>

              {values.tagType === "parent" && (
                <Grid item xs={12} md={6}>
                  <Typography sx={{ fontWeight: 600, mb: 1 }}>
                    Tag Reserve Amount
                  </Typography>
                  <TextField
                    fullWidth
                    placeholder="Enter Reserve Amount"
                    value={reserveAmount}
                    onChange={(e) => setReserveAmount(e.target.value)}
                    type="number"
                    inputProps={{ min: 0, step: 0.01 }}
                    disabled={isReadOnly}
                  />
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ mt: 1 }}
                  >
                    This amount will be reserved and not available for
                    transactions.
                  </Typography>
                </Grid>
              )}

              {/* Tag Type - Only show when creating a tag */}
              {isCreateTag && (
                <Grid item xs={12}>
                  <Typography sx={{ fontWeight: 600, mb: 1 }}>
                    Tag Type
                  </Typography>
                  <FormControl component="fieldset" sx={{ width: "100%" }}>
                    <RadioGroup
                      row
                      value={values.tagType || "standalone"}
                      onChange={handleTagTypeChange}
                      sx={{ gap: 3, mt: 1 }}
                    >
                      <FormControlLabel
                        value="standalone"
                        control={
                          <Radio
                            sx={{
                              color: primaryMain,
                              "&.Mui-checked": { color: primaryMain },
                            }}
                          />
                        }
                        label="Standalone"
                      />
                      <FormControlLabel
                        value="parent"
                        control={
                          <Radio
                            sx={{
                              color: primaryMain,
                              "&.Mui-checked": { color: primaryMain },
                            }}
                          />
                        }
                        label="Parent"
                      />
                      <FormControlLabel
                        value="child"
                        control={
                          <Radio
                            sx={{
                              color: primaryMain,
                              "&.Mui-checked": { color: primaryMain },
                            }}
                          />
                        }
                        label="Child"
                      />
                    </RadioGroup>
                  </FormControl>
                </Grid>
              )}

              {/* Show Parent ID Tag only for child tag type */}
              {values.tagType === "child" && (
                <Grid item xs={12} md={6}>
                  <Typography sx={{ fontWeight: 600, mb: 1 }}>
                    Parent ID Tag <span style={{ color: "red" }}>*</span>
                  </Typography>
                  <FormControl fullWidth required>
                    <Select
                      value={selectedParentTag}
                      onChange={handleParentTagChange}
                      displayEmpty
                    >
                      <MenuItem value="" disabled>
                        Select Parent Tag
                      </MenuItem>
                      {parentTags.map((tag) => (
                        <MenuItem key={tag.id} value={tag.id}>
                          {tag.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
              )}

              {/* Max Active Transaction Count */}
              <Grid item xs={12} md={6}>
                <Typography sx={{ fontWeight: 600, mb: 1 }}>
                  Max. Active Transaction Count
                </Typography>
                <FormControl fullWidth>
                  <Select
                    value={maxActiveType}
                    onChange={handleMaxActiveTypeChange}
                    displayEmpty
                    disabled={values.tagType === "child"}
                  >
                    <MenuItem value="block">Block all transactions</MenuItem>
                    <MenuItem value="unlimited">
                      Allow unlimited transactions
                    </MenuItem>
                    <MenuItem value="custom">Set custom limit</MenuItem>
                  </Select>
                </FormControl>
                {(maxActiveType === "block" ||
                  maxActiveType === "unlimited") && (
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ mt: 1 }}
                  >
                    {getMaxActiveHelperText()}
                  </Typography>
                )}
                {maxActiveType === "custom" && (
                  <>
                    <TextField
                      type="number"
                      label="Custom Limit"
                      value={customLimit}
                      onChange={handleCustomLimitChange}
                      fullWidth
                      sx={{ mt: 2 }}
                      inputProps={{ min: 1 }}
                      error={!!errors.max_active_trans_count}
                      disabled={values.tagType === "child"}
                    />
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ mt: 1 }}
                    >
                      Set the maximum number of concurrent transactions allowed
                      for this tag.
                    </Typography>
                  </>
                )}
              </Grid>
              {/* Expiry Date */}
              <Grid item xs={12} md={6}>
                <Typography sx={{ fontWeight: 600, mb: 1 }}>
                  Expiry Date
                </Typography>
                <LocalizationProvider dateAdapter={AdapterDateFns}>
                  <Box
                    sx={{
                      width: "100%",
                      "& .MuiInputBase-root": { width: "100%" },
                      "& .MuiTextField-root": { width: "100%" },
                    }}
                  >
                    <DatePicker
                      value={values.expirydate}
                      onChange={handleDateChange}
                      slotProps={{
                        textField: {
                          helperText:
                            touched.expirydate && formikErrors.expirydate,
                          error: Boolean(
                            touched.expirydate && formikErrors.expirydate
                          ),
                        },
                      }}
                    />
                  </Box>
                </LocalizationProvider>
              </Grid>

              {/* Notes */}
              <Grid item xs={12}>
                <Typography sx={{ fontWeight: 600, mb: 1 }}>MISC.</Typography>
                <Typography sx={{ fontWeight: 500, mb: 1 }}>Notes</Typography>
                <StyledTextarea
                  minRows={4}
                  value={values.note}
                  onChange={handleNotesChange}
                  placeholder="Enter any notes"
                />
              </Grid>
            </Grid>
          )}
          {/* EV Driver Section */}
          {tabIndex === 1 && !isCreateTag && (
            <Grid container mb={3} mt={3} className="mb-5">
              <Grid item xs={12} lg={12}>
                <Typography variant="subtitle1" className="cms-title-h6">
                  EV Driver
                </Typography>
                <Divider className="text-muted" sx={{ mt: 2, mb: 4 }} />
              </Grid>
              <Grid item xs={12} lg={12}>
                {!is.empty(tagUsers) ? (
                  <>
                    {tagUsers.map(
                      ({ userPk, email, firstName, lastName }, index) => (
                        <Grid
                          container
                          spacing={3}
                          className="cms-grid-mb"
                          key={`${index}-${firstName}-${index}`}
                        >
                          <Grid item xs={12} lg={4}>
                            <Stack
                              direction="row"
                              spacing={2}
                              alignItems="center"
                            >
                              <FormControl fullWidth size="small">
                                <TextField
                                  label="First Name"
                                  size="small"
                                  disabled={true}
                                  value={firstName}
                                />
                              </FormControl>
                            </Stack>
                          </Grid>
                          <Grid item xs={12} lg={4}>
                            <Stack
                              direction="row"
                              spacing={2}
                              alignItems="center"
                            >
                              <FormControl fullWidth size="small">
                                <TextField
                                  label="Last Name"
                                  size="small"
                                  disabled={true}
                                  value={lastName}
                                />
                              </FormControl>
                            </Stack>
                          </Grid>
                          <Grid item xs={12} lg={4}>
                            <Stack
                              direction="row"
                              spacing={2}
                              alignItems="center"
                            >
                              <FormControl fullWidth size="small">
                                <TextField
                                  label="Email"
                                  size="small"
                                  disabled={true}
                                  value={email}
                                />
                              </FormControl>
                            </Stack>
                          </Grid>
                        </Grid>
                      )
                    )}
                  </>
                ) : (
                  "No EV Driver associated with the RFID tag"
                )}
              </Grid>
            </Grid>
          )}

          {/* Attached Chargers Section */}
          {tabIndex === 2 && !isCreateTag && (
            <Grid container mb={3} mt={3} className="mb-5">
              <Grid item xs={12} lg={12}>
                <Typography variant="subtitle1" className="cms-title-h6">
                  Attached Chargers
                </Typography>
                <Divider className="text-muted" sx={{ mt: 2, mb: 4 }} />
              </Grid>
              <Grid item xs={12} lg={12}>
                {tagsChargerList && (
                  <BaseTable
                    height={250}
                    rows={tagsChargerList ?? []}
                    columns={tagFormChargersColumns()}
                    getRowId={(row) => row.charge_box_id}
                    loading={isTagsChargerFetching}
                  />
                )}
              </Grid>
            </Grid>
          )}

          {/* Attached Child tags for Parent */}
          {tabIndex === 3 && values.tagType === "parent" && (
            <Grid container>
              <Grid item xs={12}>
                <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>
                  Child RFID Tags
                </Typography>
                <BaseTable
                  height={300}
                  rows={childTags}
                  columns={[
                    { field: "tag", headerName: "Tag ID", flex: 1 },
                    {
                      field: "status",
                      headerName: "Status",
                      flex: 1,
                      valueGetter: (row) =>
                        row.active ? "Active" : "Inactive",
                    },
                    {
                      field: "expirydate",
                      headerName: "Expiry Date",
                      flex: 1,
                      sortable: true,
                      filterable: true,
                      valueGetter: (params) =>
                        params.row.expirydate
                          ? new Date(params.row.expirydate).toLocaleDateString()
                          : "-",
                    },
                  ]}
                  getRowId={(row) => row.id}
                />
              </Grid>
            </Grid>
          )}

          {/* Payment history of top ups */}
          {tabIndex === 4 && (
            <Grid container>
              <Grid item xs={12}>
                <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>
                  Topup History
                </Typography>
                <BaseTable
                  height={300}
                  rows={topupHistory}
                  loading={loadingTopupHistory}
                  columns={[
                    {
                      field: "created_at",
                      headerName: "Date",
                      flex: 1,
                      valueGetter: (params) =>
                        params.row.created_at
                          ? new Date(params.row.created_at).toLocaleString()
                          : "-",
                    },
                    { field: "amount", headerName: `Amount (${teamCurrencySymbol})`, flex: 1 },
                    { field: "status", headerName: "Status", flex: 1 },
                  ]}
                  getRowId={(row) => row.payment_id}
                />
              </Grid>
            </Grid>
          )}

          {/* Buttons */}
          {!isReadOnly && (
            <Stack direction="row" spacing={2} mt={4}>
              <LoadingButton
                type="submit"
                variant="contained"
                sx={{
                  bgcolor: primaryMain,
                  color: primaryContrast,
                  borderRadius: 2,
                  fontWeight: 600,
                  px: 4,
                  "&:hover": { bgcolor: primaryHover },
                }}
              Charging Profile Overviewading={isSubmitting}
              >
                Save
              </LoadingButton>
              <Button
                variant="outlined"
                color="inherit"
                sx={{ borderRadius: 2, fontWeight: 600, px: 4 }}
                onClick={() => navigate("/tag")}
              >
                Cancel
              </Button>
            </Stack>
          )}
        </FormikProvider>
      </Box>
      <Modal open={!isReadOnly && topupModalOpen} onClose={() => setTopupModalOpen(false)}>
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            bgcolor: theme.palette.background.paper,
            borderRadius: 3,
            boxShadow: theme.customShadows?.z24 ?? 8,
            border: `1px solid ${paperBorder}`,
            p: 4,
            minWidth: 350,
            maxWidth: 400,
            maxHeight: "80vh",
            overflowY: "auto",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
          }}
        >
          <Typography
            variant="h5"
            sx={{ mb: 2, fontWeight: 700, color: primaryMain }}
          >
            Topup Balance
          </Typography>
          <Typography sx={{ mb: 2, color: theme.palette.text.secondary, textAlign: "center" }}>
            Add funds to your RFID tag balance using secure payment gateway.
          </Typography>
          <Box
            sx={{
              backgroundColor: balanceHighlightBg,
              borderRadius: 2,
              px: 2,
              py: 1,
              mb: 2,
              width: "100%",
              textAlign: "center",
            }}
          >
            <Typography sx={{ fontWeight: 600, color: theme.palette.text.secondary }}>
              Current Balance
            </Typography>
            <Typography
              sx={{ color: primaryMain, fontWeight: 700, fontSize: 22 }}
            >
              {teamCurrencySymbol}{parseFloat(values.balance_amount || 0).toFixed(2)}
            </Typography>
          </Box>
          <TextField
            label="Topup Amount *"
            type="number"
            fullWidth
            value={topupAmount}
            onChange={(e) => setTopupAmount(e.target.value)}
            inputProps={{ min: 1 }}
            variant="outlined"
          />
          <Typography sx={{ fontSize: 12, color: theme.palette.text.secondary, mt: 1, mb: 2, textAlign: "left" }}>
            Note: 2.36% payment gateway charge applies.<br />
            You will be charged <b>{teamCurrencySymbol}{parseFloat(topupAmount || 0).toFixed(2)}</b>, and <b>{teamCurrencySymbol}{parseFloat(calculateEffectiveTopupValue(parseFloat(topupAmount || 0))).toFixed(2)}</b> will be added to your rfid tag balance.
          </Typography>
          <Typography sx={{ fontWeight: 300, fontSize: 12, mb: 3, color: theme.palette.error.main }}>
              {(() => {
                if (!topupAmount || !minWalletBalance) return "";
                
                const currentBalance =   parseFloat(values.balance_amount || 0);
                const topupValue = parseFloat(topupAmount || 0);
                const minRequired = parseFloat(minWalletBalance || 0);
                const newBalance = currentBalance + topupValue;
                
                if (newBalance < minRequired) {
                  const shortfall = minRequired - currentBalance;
                  return `You cannot add less than ${teamCurrencySymbol}${shortfall} because your balance after topup (${teamCurrencySymbol}${newBalance.toFixed(2)}) must meet the minimum wallet balance requirement of ${teamCurrencySymbol}${minRequired}`;
                }
                
                return "";
              })()}
          </Typography>
          <Stack
            direction="row"
            spacing={2}
            justifyContent="center"
            sx={{ mt: 2 }}
          >
            <Button
              variant="contained"
              sx={{
                bgcolor: primaryMain,
                color: primaryContrast,
                borderRadius: 2,
                fontWeight: 600,
                px: 4,
                boxShadow: theme.customShadows?.z8 ?? 2,
                "&:hover": { bgcolor: primaryHover },
              }}
              disabled={
                isReadOnly ||
                !topupAmount || 
                topupInProgress || 
                (() => {
                  if (!topupAmount || !minWalletBalance) return false;
                  const currentBalance = parseFloat(values.balance_amount || 0);
                  const topupValue = parseFloat(topupAmount || 0);
                  const minRequired = parseFloat(minWalletBalance || 0);
                  const newBalance = currentBalance + topupValue;
                  return newBalance < minRequired;
                })()
              }
              onClick={async () => {
                await handleTopup(topupAmount);
              }}
            >
              Pay {teamCurrencySymbol}{topupAmount || 0}
            </Button>
            <Button
              variant="outlined"
              color="inherit"
              sx={{
                borderRadius: 2,
                fontWeight: 600,
                px: 4,
                boxShadow: 1,
              }}
              onClick={() => setTopupModalOpen(false)}
              disabled={topupInProgress || isReadOnly}
            >
              Cancel
            </Button>
          </Stack>
          {topupInProgress && (
            <Typography sx={{ mt: 2, color: primaryMain, fontWeight: 500 }}>
              Processing top-up, please wait...
            </Typography>
          )}
        </Box>
      </Modal>
    </Box>
  );
}

export default TagForm;
